import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import PageByPage from "./PageByPage";
import AtAGlance from "./AtAGlance";
import Views from "./Views";
import Viewers from "./Viewers";
import { Container, Row, Col, Form } from "react-bootstrap";
import CustomTooltip from "../../Elements/Tooltip";
import LaserLinkModal from "../../Elements/LaserLink/LaserLinkModal";
import Viewer from "./Viewer/Viewer";
import Session from "./Session/Session";
import StatsNavTabs from "./StatsNavTabs";
import KdfSelector from "./KdfSelector";
import TlSelector from "./TlSelector";
import RecipientSelector from "./RecipientSelector";
import LaserLinkSelector from "./LaserLinkSelector";
import { useStats } from "./StatsContext";

const RenderComponent = () => {
  const { pathname, hash } = useLocation();
  const { tl, doc } = window?.objInit;

  if (hash.includes("/page-by-page")) {
    return <PageByPage />;
  } else if (hash.includes("/at-a-glance")) {
    return <AtAGlance />;
  } else if (hash.includes("/views")) {
    return <Views />;
  } else if (hash.includes("/viewers")) {
    return <Viewers />;
  } else if (hash.includes("/viewer/")) {
    return <Viewer />;
  } else if (hash.includes("/session/")) {
    return <Session />;
  } else {
    if (pathname.includes(tl?.token || doc?.group_id)) {
      window.location.href = `/${pathname?.split("/")[1]}/${tl?.token || doc?.group_id
        }#/at-a-glance`;
    }
    return <>Nothing Found {JSON.stringify(pathname)}</>;
  }
};

const StatsView = ({ time_zone_list }) => {
  const [selectedKDFLink, setSelectedKDFLink] = useState(null);
  const { tl, pdfFile, doc } = window?.objInit;
  const { statsData, chartStatsLoading, fetchChartStats, selectedDocsFileId, setSelectedDocsFileId } = useStats();
  const data = tl || statsData.doc || doc;
  const currentPdfFile = statsData.pdfFile || pdfFile;
  const { hash, pathname } = useLocation();
  const isKdf = pathname.includes("kdf-stats");
  const [thresholdValue, setThresholdValue] = useState(data?.page_viewed_sec ?? "");

  const handleThresholdChange = (e) => {
    const val = e.target.value;
    const num = val === "" ? null : Number(val);
    if (num !== null && (Number.isNaN(num) || num < 1)) return;
    setThresholdValue(val);
  };

  const handleThresholdBlur = () => {
    if (thresholdValue) fetchChartStats(thresholdValue, selectedDocsFileId);
  };

  const handleThresholdKeyDown = (e) => {
    if (e.key === "Enter") {
      e.target.blur();
    }
  };

  // Re-fetch chart stats when language filter changes
  const langInitRef = useRef(true);
  useEffect(() => {
    if (langInitRef.current) { langInitRef.current = false; return; }
    if (thresholdValue) {
      // Pass selectedDocsFileId directly — ref may not be updated yet (child effects run before parent)
      fetchChartStats(thresholdValue, selectedDocsFileId);
    }
  }, [selectedDocsFileId]);

  return (
    <>
      <LaserLinkModal
        laserItem={selectedKDFLink}
        time_zone_list={time_zone_list}
        updateSelectedKDFLink={setSelectedKDFLink}
      />
      <Container
        className={`bg-light-container ${hash.includes("/page-by-page") ? "page-by-page-pading" : ""
          }`}
      >
        <div className="laser-visit mb-3 p-3" style={{ position: 'relative' }}>
        {isKdf && (
          <p className="mb-2">
            <span>Total Pages: </span>
            {currentPdfFile?.num_pages ?? "-"}
          </p>
        )}
        <Row>
              <Col md={6}>
                <div className="mb-2">
                  {isKdf ? (
                    <>
                      <span className="me-3 d-flex align-items-center">
                        KDF:{" "}
                      </span>
                      <div className="mt-2 mb-2">
                        <KdfSelector />
                      </div>

                      {doc?.doc_files_languages?.length > 1 && (
                        <>
                      <span className="me-3 d-flex align-items-center">
                        Language:{" "}
                      </span>
                        <div className="d-flex align-items-center gap-2 mt-2 mb-2 flex-wrap">
                          <span
                            className={`cursor-pointer ${!selectedDocsFileId ? 'lang-filter-active' : ''}`}
                            style={{
                              padding: '5px 20px',
                              fontSize: '14px',
                              fontWeight: !selectedDocsFileId ? 600 : 500,
                              borderRadius: '4px',
                              backgroundColor: !selectedDocsFileId ? '#294561' : 'transparent',
                              textDecoration: 'none',
                              whiteSpace: 'nowrap',
                              display: 'inline-block',
                            }}
                            onClick={() => setSelectedDocsFileId(null)}
                          >
                            All
                          </span>
                          {doc.doc_files_languages
                            .filter(f => f.status === 'active')
                            .map(f => {
                              const hasVisits = f.has_visits !== false;
                              return (
                                <span
                                  key={f.file_id}
                                  className={hasVisits ? "cursor-pointer" : ""}
                                  onClick={() => hasVisits && setSelectedDocsFileId(f.file_id)}
                                  title={hasVisits ? f.language?.name : `${f.language?.name} (No visits)`}
                                  style={{
                                    padding: '5px 12px',
                                    borderRadius: '4px',
                                    backgroundColor: selectedDocsFileId === f.file_id ? '#294561' : 'transparent',
                                    opacity: !hasVisits ? 0.3 : (selectedDocsFileId && selectedDocsFileId !== f.file_id ? 0.5 : 1),
                                    cursor: hasVisits ? 'pointer' : 'not-allowed',
                                  }}
                                >
                                  <span className={f.language?.class} style={{ width: '24px', display: 'inline-block' }}></span>
                                </span>
                              );
                            })}
                        </div>
                        </>
                      )}

                      {data?.page_viewed_sec != null &&
                        (hash.includes("/page-by-page") ||
                          hash.includes("/at-a-glance") ||
                          hash.includes("/viewer/") ||
                          hash.includes("/views") ||
                          hash.includes("/viewers")) ? (
                        <div className="d-flex align-items-center mt-2">
                          <CustomTooltip
                            title={
                              <p className="p-1 text-light">
                                Display recipients who actively viewed this KDF for at
                                least this amount of time per page.
                              </p>
                            }
                          >
                            <span>Engagement Threshold: </span>
                          </CustomTooltip>
                          <CustomTooltip
                            title={
                              <p className="p-1 text-light">
                                Click on the number to adjust the engagement threshold.
                              </p>
                            }
                          >
                            <span className="d-inline-flex align-items-center ms-1">
                              <Form.Control
                                type="number"
                                min={1}
                                value={thresholdValue}
                                onChange={handleThresholdChange}
                                onBlur={handleThresholdBlur}
                                onKeyDown={handleThresholdKeyDown}
                                disabled={chartStatsLoading}
                                style={{ width: "60px", height: "38px", padding: "2px 6px", fontSize: "13px" }}
                              />
                              <span className="ms-1">{chartStatsLoading ? "loading..." : "seconds"}</span>
                            </span>
                          </CustomTooltip>
                        </div>
                      ) : (
                        ""
                      )}

                    </>

                  ) : (
                    <>
                      <span className="me-3 d-flex align-items-center">
                        LaserLINK:{" "}
                      </span>
                      <div className="mt-2 mb-2">
                        <TlSelector />
                      </div>

                      <span className="me-3 d-flex align-items-center">
                        Recipient:{" "}
                      </span>
                      <div className="mt-2 mb-2">
                        <RecipientSelector />
                      </div>
                    </>
                  )}

                </div>

                {
                  !isKdf &&
                  <div>
                    <span>
                      {(data?.tracking_link?.landing_page ||
                        data?.landing_page) &&
                        pdfFile.num_pages > 1 && (
                          <CustomTooltip
                            title={
                              <p className="p-1 text-light">
                                The landing page is page {data?.tracking_link?.landing_page ?? data?.landing_page}
                              </p>
                            }
                          >
                            <span style={{ color: "#787979" }} className="me-2">
                              LP={data?.tracking_link?.landing_page ?? data?.landing_page}
                            </span>
                          </CustomTooltip>
                        )}
                      {(data?.tracking_link?.disable_settings?.disable_for ||
                        data?.disable_settings?.disable_for) && (
                          <CustomTooltip
                            title={
                              <p className="p-1 text-light">
                                The scrolling delay parameter is set to {data?.tracking_link?.disable_settings?.disable_for ?? data?.disable_settings?.disable_for} seconds.
                              </p>
                            }
                          >
                            <span style={{ color: "#787979" }} className="me-2">
                              SD=
                              {data?.tracking_link?.disable_settings?.disable_for ?? data?.disable_settings?.disable_for}
                            </span>
                          </CustomTooltip>
                        )}
                      {data?.opt_in?.show_on_page && (
                        <CustomTooltip
                          title={
                            <p className="p-1 text-light">
                              The opt-in is set to trigger on page {data?.has_optin ? data?.opt_in?.show_on_page : ""}.
                            </p>
                          }
                        >
                          <span style={{ color: "#787979" }} className="me-2">
                            OI={data?.has_optin ? data?.opt_in?.show_on_page : ""}
                          </span>
                        </CustomTooltip>
                      )}
                      {/* {data?.has_optin && (
                    <span style={{ color: "#787979" }} className="me-2">
                      OI={data?.opt_in?.show_on_page ?? ""}
                    </span>
                  )} */}
                    </span>
                    {(data?.domain || data?.tracking_link?.domain) && (
                      <CustomTooltip
                        title={
                          <p className="p-1 text-light">
                            The domain this laser link uses is {(data?.domain || data?.tracking_link?.domain) ?? "-"}.
                          </p>
                        }
                      >
                        <span>
                          Domain:{" "}
                          {(data?.domain || data?.tracking_link?.domain) ?? "-"}
                        </span>
                      </CustomTooltip>
                    )}
                    {data?.tags?.length > 0
                      ? data?.tags?.map((tag, tagIndex) => (
                        <span
                          key={`${tag?.id}`}
                          className="tag-primary text-white"
                        >
                          {tag?.name || "-"}
                        </span>
                      ))
                      : ""}
                  </div>
                }
                {!isKdf && data?.page_viewed_sec != null &&
                  (hash.includes("/page-by-page") ||
                    hash.includes("/at-a-glance") ||
                    hash.includes("/viewer/") ||
                    hash.includes("/views") ||
                    hash.includes("/viewers")) ? (
                  <div className="d-flex align-items-center mt-2">
                    <CustomTooltip
                      title={
                        <p className="p-1 text-light">
                          Display recipients who actively viewed this KDF for at
                          least this amount of time per page.
                        </p>
                      }
                    >
                      <span>Engagement Threshold: </span>
                    </CustomTooltip>
                    <CustomTooltip
                      title={
                        <p className="p-1 text-light">
                          Click on the number to adjust the engagement threshold.
                        </p>
                      }
                    >
                      <span className="d-inline-flex align-items-center ms-1">
                        <Form.Control
                          type="number"
                          min={1}
                          value={thresholdValue}
                          onChange={handleThresholdChange}
                          onBlur={handleThresholdBlur}
                          onKeyDown={handleThresholdKeyDown}
                          disabled={chartStatsLoading}
                          style={{ width: "60px", height: "38px", padding: "2px 6px", fontSize: "13px" }}
                        />
                        <span className="ms-1">{chartStatsLoading ? "loading..." : "seconds"}</span>
                      </span>
                    </CustomTooltip>
                  </div>
                ) : (
                  ""
                )}
              </Col>
              <Col md={6}>
                {!isKdf ? (
                  <>
                    <div>
                      <CustomTooltip
                        title={
                          <p className="p-1 text-light">
                            Display all LaserLINX for this KDF
                          </p>
                        }
                      >
                        <a href={`/links/doc/${tl?.doc?.group_id}`} target="_blank">
                          <span className="custom-link-success cursor-pointer">
                            KDF:{" "}
                          </span></a>
                      </CustomTooltip>
                      <CustomTooltip
                        title={<p className="p-1 text-light">KDF Settings</p>}
                      >
                        <a href={`/kdfs/${tl?.doc?.id}`} target="_blank">
                          {tl?.doc?.title ?? "-"}
                        </a>
                      </CustomTooltip>
                    </div>
                    <p>
                      <span>Total Pages: </span>
                      {pdfFile?.num_pages ?? "-"}
                    </p>
                    <div style={{ position: 'absolute', bottom: '16px', right: '16px' }}>
                      <CustomTooltip
                        title={<p className="p-1 text-light">LaserLINK Settings</p>}
                      >
                        <span
                          className="cursor-pointer"
                          style={{
                            fontSize: "14px",
                            textDecoration: "underline",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            fontWeight: "500",
                            color: "#294561"
                          }}
                          onClick={() => setSelectedKDFLink(data)}
                        >
                          <span style={{ fontSize: "16px" }}>⚙</span>
                          Open LaserLINK Settings
                        </span>
                      </CustomTooltip>
                    </div>
                  </>
                ) : (
                  <>
                    {(data?.tracking_links?.length > 0) && (
                      <>
                        <span className="me-3 d-flex align-items-center">
                          LaserLINX:{" "}
                        </span>
                        <div className="mt-2 mb-2">
                          <LaserLinkSelector trackingLinks={data.tracking_links} />
                        </div>
                      </>
                    )}
                    <span className="me-3 d-flex align-items-center">
                      Recipient:{" "}
                    </span>
                    <div className="mt-2 mb-2">
                      <RecipientSelector />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                      <CustomTooltip
                        title={<p className="p-1 text-light">KDF Settings</p>}
                      >
                        <a
                          href={`/kdfs/${data?.id}`}
                          target="_blank"
                          style={{
                            fontSize: "14px",
                            textDecoration: "underline",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            fontWeight: "500",
                            color: "#294561"
                          }}
                        >
                          <span style={{ fontSize: "16px" }}>⚙</span>
                          Open KDF Settings
                        </a>
                      </CustomTooltip>
                    </div>
                  </>
                )}
              </Col>
        </Row>
        </div>
        <StatsNavTabs />
        <RenderComponent />
      </Container>
    </>
  );
};

export default StatsView;
